import type { WorkspaceRole } from '@/lib/settings';
import { useMembersQuery, useProfileQuery } from '../queries';

/**
 * Whether the viewer may manage this workspace's team — invite, change a
 * role, remove a member.
 *
 * Workspace admins AND owners manage the team; the Admin role option is
 * org-gated. Owners hold a real `owner` row per workspace (not `admin`), so
 * both must be checked or an owner is locked out of their own team.
 *
 * **This is a workspace role, not an org role** — it comes from the caller's
 * own row in the members list, so `useOrgRole()` is not a substitute. An org
 * editor can be a workspace admin, and vice versa.
 *
 * Extracted so the command palette's "Invite teammate" quick action gates on
 * the *same* predicate `WorkspaceAccessSection` uses to render the invite
 * card, rather than re-deriving its own copy that can drift. The underlying
 * members query is shared through TanStack Query's cache, so the second
 * caller costs no extra request once either has loaded it.
 *
 * `isResolving` matters for the same reason it does in `useCreateApiKeyGate`:
 * `false` while the members list is still in flight is indistinguishable
 * from a resolved editor, which is fine for hiding a CTA but wrong for any
 * branch that TELLS the user they lack permission.
 */
export function useCanManageTeam(workspaceId: number | null | undefined): {
  /** The viewer's role in THIS workspace, or null if they hold no row. */
  myRole: WorkspaceRole | null;
  canManageTeam: boolean;
  isResolving: boolean;
} {
  const profile = useProfileQuery().data ?? null;
  const membersQuery = useMembersQuery(workspaceId);
  const members = membersQuery.data ?? [];

  const myRole = members.find((m) => m.userId === profile?.id)?.role ?? null;

  return {
    myRole,
    canManageTeam: myRole === 'admin' || myRole === 'owner',
    isResolving: workspaceId != null && membersQuery.isPending,
  };
}
